import React, { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  TopToolbar,
  FilterButton,
  ExportButton,
  TextInput,
  SelectInput,
  useGetOne,
  FunctionField,
} from "react-admin";
import { Chip, Button, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSearchParams } from "react-router-dom";
import VerifyEditDialog from "./verify-edit-dialog";
import { VerifyRecord } from "../../models/common";

type VerifyUserRecord = Omit<VerifyRecord, "of"> & {
  role?: number;
  user_type?: number;
  of?:
    | string
    | {
        role?: number;
        user_type?: number;
      };
};

// 认证状态选项
const verifyStatusOptions = [
  { id: 0, name: "待审核" },
  { id: 1, name: "已通过" },
  { id: -1, name: "未通过" },
];

// 修改状态显示组件
const StatusField = ({ record }: { record: VerifyRecord }) => {
  const status = record.status;
  const getStatusColor = (status: number) => {
    switch (status) {
      case 0:
        return "warning";
      case 1:
        return "success";
      case -1:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: number) => {
    const option = verifyStatusOptions.find((opt) => opt.id === status);
    return option ? option.name : "未知状态";
  };

  return (
    <Chip
      label={getStatusText(status)}
      color={getStatusColor(status)}
      size="small"
    />
  );
};

const IdentityField = ({ record }: { record: VerifyUserRecord }) => {
  const relatedUser =
    typeof record.of === "object" && record.of !== null ? record.of : undefined;
  const roleValue =
    record.user_type ??
    record.role ??
    relatedUser?.user_type ??
    relatedUser?.role;
  const isOwner = roleValue === 1;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontWeight: 600,
        color: isOwner ? "warning.dark" : "text.primary",
        backgroundColor: isOwner ? "warning.light" : "transparent",
      }}
    >
      {isOwner ? "货主" : "司机"}
    </Box>
  );
};

// 筛选器
const VerifyListFilters = [
  <TextInput source="name" label="姓名" alwaysOn />,
  <TextInput source="idNo" label="身份证号" />,
  <TextInput source="phone" label="手机号" alwaysOn />,
  <SelectInput
    source="status"
    label="认证状态"
    choices={verifyStatusOptions}
  />,
];

// 工具栏
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

// 修改 ActionButtons 的类型定义
const ActionButtons = ({
  record,
  onView,
}: {
  record?: VerifyRecord;
  onView: (record: VerifyRecord) => void;
}) => {
  if (!record) return null;

  return (
    <Box>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onView(record);
        }}
        startIcon={<VisibilityIcon />}
        size="small"
        sx={{ mr: 1 }}
      >
        状态变更
      </Button>
    </Box>
  );
};

const VerifyUsersList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const verifyIdFromQuery = searchParams.get("verifyId");
  const [selectedVerify, setSelectedVerify] = useState<VerifyRecord | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: verifyFromQuery } = useGetOne<VerifyRecord>(
    "verify/user",
    { id: verifyIdFromQuery ?? "" },
    { enabled: Boolean(verifyIdFromQuery) },
  );

  /**
   * 支持从通知深链 `?verifyId=` 直接打开审核弹窗。
   */
  useEffect(() => {
    if (!verifyIdFromQuery) {
      return;
    }
    if (verifyFromQuery) {
      setSelectedVerify(verifyFromQuery);
      setDialogOpen(true);
      return;
    }
    setSelectedVerify({ id: verifyIdFromQuery } as VerifyRecord);
    setDialogOpen(true);
  }, [verifyIdFromQuery, verifyFromQuery]);

  const handleOpenDialog = (record: VerifyRecord) => {
    setSelectedVerify(record);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVerify(null);
    if (searchParams.has("verifyId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("verifyId");
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <>
      <List
        actions={<ListActions />}
        filters={VerifyListFilters}
        sort={{ field: "createdAt", order: "DESC" }}
        perPage={10}
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="name" label="姓名" />
          <FunctionField<VerifyUserRecord>
            label="身份"
            render={(record) => <IdentityField record={record} />}
          />
          <TextField source="idNo" label="身份证号" />
          <TextField source="phone" label="手机号" />
          <FunctionField<VerifyRecord>
            label="认证状态"
            render={(record) => <StatusField record={record} />}
          />
          <DateField source="createdAt" label="申请时间" showTime />
          <DateField source="updatedAt" label="更新时间" showTime />
          <FunctionField<VerifyRecord>
            label="操作"
            render={(record) => (
              <ActionButtons record={record} onView={handleOpenDialog} />
            )}
          />
        </Datagrid>
      </List>

      <VerifyEditDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        verifyId={selectedVerify?.id ?? ""}
        verifyData={selectedVerify}
      />
    </>
  );
};

export default VerifyUsersList;
